import { useEffect, useRef, useState } from "react";
import { SearchUI } from "src/components/ui/Search";
import { searchPeopleRequest } from "src/components/ui/form/peoplePicker/api";
import { useFiltersContext } from "src/contexts/FilterContext";
import { CheckBoxListItem, SearchPeopleType, UserListItem } from "src/types/main";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { getNameAndSurname, nameFromEmail, prefix, removePrefix, shortNameFromEmail } from "src/utils/users.utils";
import { EquipmentKeys, FiltersStateType, Keys, ReportFilterKeys, RestKeys, UserKeys } from "../../type";
import { CheckEquipmentsList, CheckList, CheckUsersList, TGCheckList } from "./CheckLists";
import { equipmentsListName } from "src/api/config";

export function ListWithSearch({
  keyItem,
  items,
}: {
  keyItem: Keys | RestKeys | ReportFilterKeys;
  items: CheckBoxListItem[];
}) {
  const { filters, addFilter, removeFilter } = useFiltersContext();

  const [list, setList] = useState<CheckBoxListItem[]>(items);

  function searchItems(query: string) {
    const results = items.filter((d) => d.title && d.title.toLowerCase().includes(query.toLowerCase()));

    if (query === "") {
      setList(items);
    } else {
      setList(results);
    }
  }

  function handleChecked(item: CheckBoxListItem) {
    const isFound = filters[keyItem].find((el) => el.id === item.id);

    if (isFound) {
      removeFilter(keyItem, item);
    } else {
      addFilter(keyItem, item);
    }
  }

  useEffect(() => {
    setList(items);
  }, [items]);

  return (
    <FlexRow className="flex-wrap gap-y-[15px]">
      <FlexCol className="w-full">
        <SearchUI placeholder="Поиск..." onClick={searchItems} />
      </FlexCol>
      <FlexCol className="w-full">
        <CheckList keyItem={keyItem} items={list} onChecked={handleChecked} />
      </FlexCol>
    </FlexRow>
  );
}

export function filterUserSearchResults(response: SearchPeopleType[]): UserListItem[] {
  return response.reduce((acc, user) => {
    const email = removePrefix(user.AccountName, prefix);
    const obj = {
      id: user.DocId,
      email: email,
      title: `${getNameAndSurname(user.PreferredName, user.WorkEmail) || nameFromEmail(email)} (${
        user.apiGlobalID || "Нет номера"
      })`,
      avatar: user.PictureURL,
      shortName: shortNameFromEmail(email),
    };
    acc.push(obj);
    return acc;
  }, [] as UserListItem[]);
}

export function ListUserWithSearch({ keyItem, items }: { keyItem: UserKeys; items: UserListItem[] }) {
  const { filters, addFilter, removeFilter } = useFiltersContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [list, setList] = useState<UserListItem[]>(items);

  async function searchItems(query: string) {
    setError(false);

    if (query === "" || query.length <= 3) {
      setList(list);
      return;
    }

    try {
      setLoading(true);
      const response = (await searchPeopleRequest(query.trim())) as unknown as SearchPeopleType[];

      if (response) {
        const dataList = filterUserSearchResults(response);
        if (dataList.length > 0) {
          setList(dataList);
        } else {
          setList([]);
        }
      }
    } catch (error) {
      console.error("ошибка поиска пользователя");
      setList([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function handleChecked(item: UserListItem) {
    const isFound = filters[keyItem].find((el) => el.id === item.id) as UserListItem;

    if (isFound) {
      removeFilter(keyItem, item);
    } else {
      addFilter(keyItem, item);
    }
  }

  useEffect(() => {
    setList(items);
  }, [items]);

  return (
    <FlexRow className="flex-wrap gap-y-[15px]">
      <FlexCol className="w-full">
        {error && <span className="text-rose-500 text-[11px]">Введите больше символов</span>}
        <SearchUI placeholder="Поиск..." onClick={searchItems} loading={loading} />
      </FlexCol>
      <FlexCol className="w-full">
        <CheckUsersList keyItem={keyItem} items={list} onChecked={handleChecked} />
      </FlexCol>
    </FlexRow>
  );
}

export function ListEquipmentsWithSearch({ keyItem, items }: { keyItem: EquipmentKeys; items: CheckBoxListItem[] }) {
  const { filters, addFilter, removeFilter } = useFiltersContext();
  const inputRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [list, setList] = useState<CheckBoxListItem[]>(items);

  async function searchItems(query: string) {
    setError(false);

    if (query === "" || query.length <= 1) {
      setList(list);
      return;
    }

    try {
      setLoading(true);

      let equipments: CheckBoxListItem[] = await fetchEquipments(keyItem, query, filters);

      if (equipments.length > 0) {
        inputRef.current = query;
        setList(equipments);
      } else {
        setList([]);
      }
    } catch (error) {
      console.error("ошибка поиска оборудования");
      setList([]);

      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function handleChecked(item: CheckBoxListItem) {
    const isFound = filters[keyItem].find((el) => el.id === item.id) as CheckBoxListItem;

    if (isFound) {
      removeFilter(keyItem, item);
    } else {
      addFilter(keyItem, item);
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      searchItems(inputRef.current);
    }
  }, [
    filters.collections,
    filters.manufacturer,
    filters.model,
    filters.titleEquipment,
    filters.typeEquipment,
    filters.sap,
  ]);

  // useEffect(() => {
  //   setList(items);
  // }, [items]);

  return (
    <FlexRow className="flex-wrap gap-y-[15px]">
      <FlexCol className="w-full">
        {error && <span className="text-rose-500 text-[11px]">Введите больше символов</span>}
        <SearchUI
          placeholder="Поиск..."
          onClick={searchItems}
          resetHandle={() => setList([])}
          loading={loading}
          delay={1000}
        />
      </FlexCol>
      <FlexCol className="w-full">
        <CheckEquipmentsList keyItem={keyItem} items={list} onChecked={handleChecked} />
      </FlexCol>
    </FlexRow>
  );
}

function buildFilterQuery(filters: FiltersStateType) {
  const filterConditions = [];

  if (filters.collections && filters.collections.length > 0) {
    const collectionFilter = filters.collections.map((cq) => `substringof('${cq.title}', Collection)`).join(" or ");
    filterConditions.push(`(${collectionFilter})`);
  }

  if (filters.model && filters.model.length > 0) {
    const modelFilter = filters.model.map((model) => `substringof('${model.title}', Model)`).join(" or ");
    filterConditions.push(`(${modelFilter})`);
  }

  if (filters.typeEquipment && filters.typeEquipment.length > 0) {
    const typeEquipmentFilter = filters.typeEquipment
      .map((type) => `substringof('${type.title}', TypeEquipment)`)
      .join(" or ");
    filterConditions.push(`(${typeEquipmentFilter})`);
  }

  if (filters.sap && filters.sap.length > 0) {
    const sapFilter = filters.sap.map((sap) => `substringof('${sap.title}', SAP)`).join(" or ");
    filterConditions.push(`(${sapFilter})`);
  }

  if (filters.manufacturer && filters.manufacturer.length > 0) {
    const manufacturerFilter = filters.manufacturer
      .map((manufacturer) => `substringof('${manufacturer.title}', Manufacturer)`)
      .join(" or ");
    filterConditions.push(`(${manufacturerFilter})`);
  }

  if (filters.titleEquipment && filters.titleEquipment.length > 0) {
    const titleEquipmentFilter = filters.titleEquipment
      .map((titleEquipment) => `substringof('${titleEquipment.title}', Title)`)
      .join(" or ");
    filterConditions.push(`(${titleEquipmentFilter})`);
  }

  return filterConditions.join(" and ");
}

const equipmentKeyMap: Record<string, string> = {
  manufacturer: "Manufacturer",
  model: "Model",
  sap: "SAP",
  typeEquipment: "TypeEquipment",
  titleEquipment: "Title",
};

async function fetchEquipments(keyItem: string, query: string, filters: FiltersStateType): Promise<CheckBoxListItem[]> {
  const baseUrl = `/_api/web/lists/getbytitle('${equipmentsListName}')/items?$select=ID,Title,SAP,Manufacturer,Model,TypeEquipment,Collection`;
  let filterQuery = "";

  switch (keyItem) {
    case "manufacturer":
    case "model":
    case "sap":
    case "typeEquipment":
      filterQuery = `substringof('${query}', ${equipmentKeyMap[keyItem]})`;
      break;
    default:
      filterQuery = `substringof('${query}', Title)`;
      break;
  }

  const additionalFilters = buildFilterQuery(filters);

  if (additionalFilters) {
    filterQuery = `(${filterQuery}) and (${additionalFilters})`;
  }

  const fullUrl = `${baseUrl}&top=100&$filter=${filterQuery}`;
  //* console.log("Generated URL:", fullUrl);

  try {
    const response = await fetch(fullUrl, {
      method: "GET",
      headers: {
        Accept: "application/json;odata=verbose",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    const mappedData: CheckBoxListItem[] = data.d.results.map((r: Record<string, string>) => ({
      id: r.ID,
      title: r[equipmentKeyMap[keyItem]],
    }));

    return mappedData;
  } catch (error) {
    const responseError = error as Error;

    console.error("Error fetching data:", responseError);
    throw responseError;
  }
}

export function TGListWithSearch({
  listData,
  checkedItems,
  items,
  initSearch = "",
  localSearch,
  isCheckedSort = false,
  listLoading = false,
}: {
  listData: CheckBoxListItem[];
  checkedItems: (item: CheckBoxListItem) => void;
  items: CheckBoxListItem[];
  initSearch?: string;
  localSearch?: (query: string) => Promise<CheckBoxListItem[]>;
  isCheckedSort?: boolean;
  listLoading?: boolean;
}) {
  const [search, setSearch] = useState(initSearch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [list, setList] = useState<CheckBoxListItem[]>(listData);

  async function searchItems(query: string) {
    setError(false);
    if (localSearch) {
      setLoading(true);
      try {
        const filterList = await localSearch(query);
        setList(filterList);
      } catch (error) {
        console.error(error);
        setError(true);
      } finally {
        setLoading(false);
      }
    } else {
      if (query) {
        const filterList = list.filter((l) => l.title.toLowerCase().includes(query.toLowerCase()));
        setList(filterList);
      } else {
        setList(listData);
      }
    }
  }

  useEffect(() => {
    if (listData.length) {
      setList(listData);
    }
  }, [listData]);

  useEffect(() => {
    searchItems(search);
  }, [search]);

  return (
    <FlexRow className="flex-wrap gap-y-[15px]">
      <FlexCol className="w-full">
        {error && <span className="text-rose-500 text-[11px]">Введите больше символов</span>}
        <SearchUI
          placeholder="Поиск..."
          onClick={(query) => setSearch(query)}
          resetHandle={() => setList([])}
          loading={loading}
          delay={1000}
          emptyValue={true}
        />
      </FlexCol>
      <FlexCol className="w-full">
        <TGCheckList
          list={list}
          items={items}
          onChecked={checkedItems}
          isCheckedSort={isCheckedSort}
          loading={listLoading}
        />
      </FlexCol>
    </FlexRow>
  );
}
